import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { api, isSpendApprovalError } from './api';
import { US_STATES } from './shared/StateSelect';
import { toast } from 'sonner';
import {
  Calculator, Send, Loader2, Save, RefreshCcw, FileText,
  CheckCircle2, Clock, XCircle, ExternalLink, Copy, Home, ChevronDown, ChevronRight,
  Sparkles, Download, FileSignature, Trash2,
} from 'lucide-react';
import DocuSignEnvelopeModal from './DocuSignEnvelopeModal';
import { copyText } from './clipboard';
import MoneyInput from './shared/MoneyInput';
import NumberInput from './shared/NumberInput';
import PickSelect from './shared/PickSelect';
import TaxLookupPanel from './feeWorksheet/TaxLookupPanel';
import AddressAutocomplete from './feeWorksheet/AddressAutocomplete';
import { getCounties } from './feeWorksheet/propertyTaxApi';
import ContactSearchInput, { primaryEmail } from './shared/ContactSearchInput';
import QuickContactForm from './shared/QuickContactForm';
import { uc } from './unifiedContactsApi';
import { errorMessage } from './errors';
import { useAuth } from './AuthContext';
import { formatDateTime } from './kit/time';
import { confirmDialog } from '@/components/dialogs/DialogHost';
import { Input } from '../components/ui/input';

// Loan-file addresses are Address objects; older records may hold a string.

// Ticket 155: what the "+ New contact" form is prefilled with, from the text typed
// in the Borrower name box (an "@" means it is an email, otherwise first/last on the first space).
const contactSeed = (text) => {
  const t = (text || '').trim();
  if (t.includes('@')) return { email: t };
  const i = t.indexOf(' ');
  return i < 0 ? { first_name: t } : { first_name: t.slice(0, i), last_name: t.slice(i + 1).trim() };
};

const NAVY = '#070B5F';
const GREEN = '#6BA82E';
const LIME = '#A6E56C';

// Ticket 77: county lists keyed by state. TX has curated fee tables; LA/FL
// (Ken's other states) get a picker but run TX placeholder rates until real
// tables exist. Any other state gets a free-text county box.
const COUNTIES_BY_STATE = {
  TX: ['Travis', 'Harris', 'Dallas', 'Tarrant', 'Bexar', 'Collin', 'Denton', 'Fort Bend', 'Williamson', 'Montgomery', 'Hays'],
  LA: ['Orleans', 'Jefferson', 'East Baton Rouge', 'St. Tammany', 'Lafayette', 'Caddo', 'Calcasieu', 'Ouachita', 'Rapides', 'Bossier'],
  FL: ['Miami-Dade', 'Broward', 'Palm Beach', 'Hillsborough', 'Orange', 'Pinellas', 'Duval', 'Lee', 'Polk', 'Brevard', 'Volusia', 'Pasco', 'Seminole', 'Sarasota', 'Collier'],
};
const TX_COUNTIES_FALLBACK = COUNTIES_BY_STATE.TX;
const withoutTaxRate = (overrides) => Object.fromEntries(Object.entries(overrides || {}).filter(([k]) => k !== 'property_tax_rate_pct'));
const COUNTY_NOUN = { LA: 'Parish' };
const CURATED_STATES = ['TX'];
const PROGRAMS = ['Conventional', 'FHA', 'VA', 'USDA', 'Jumbo', 'Non-QM'];
// Ticket 198: loan terms the server accepts (years). A worksheet saved before the term existed reads as 30.
const LOAN_TERMS = [10, 15, 20, 25, 30];
const DEFAULT_TERM = 30;
const TERM_OPTIONS = LOAN_TERMS.map((t) => ({ value: String(t), label: `${t} years` }));
const DECIDED = ['approved', 'rejected'];
const SAVED_PREVIEW = 10; // rows of "Recent worksheets" shown before "Show all"

// Down payment in dollars for a price and a percent; the loan amount is price minus this.
const dpOf = (price, pct) => Math.round(Number(price) * (Number(pct) / 100));
// The percent a loan amount leaves over (6 decimals), 0 when the loan covers the price. The
// server works cash to close out from this percent, so it must follow a hand-typed loan amount.
const pctFor = (price, loan) => (Number(price) > Number(loan) && Number(price) > 0
  ? Math.round(((Number(price) - Number(loan)) / Number(price)) * 1e8) / 1e6 : 0);
// Only a plain id goes into the request path: a crafted ?worksheet= must not reach another route.
const WORKSHEET_ID_RE = /^[A-Za-z0-9_-]{1,64}$/;
const decidedAt = formatDateTime;

const fmt$ = (n) => (n == null ? '—' : n.toLocaleString('en-US', {
  style: 'currency', currency: 'USD', maximumFractionDigits: 0,
}));
const fmt$2 = (n) => (n == null ? '—' : n.toLocaleString('en-US', {
  style: 'currency', currency: 'USD', minimumFractionDigits: 2,
}));

// ===========================================================================
// Ticket 192 (grill 186 D8): a loan's fees live on the loan file, so the old
// "opened from a loan" entry (?loan=) goes to the loan file's Review Fees screen.
// This page stays the estimator for prospects with no loan file.
export default function FeeWorksheetPage() {
  const [params] = useSearchParams();
  const loanId = params.get('loan');
  if (loanId) return <Navigate to={`/portal/loans/${encodeURIComponent(loanId)}?section=review_fees`} replace />;
  return <FeeWorksheetEditor />;
}

function FeeWorksheetEditor() {
  const [inputs, setInputs] = useState({
    purchase_price: 425000,
    loan_amount: 340000,
    state: '',            // ticket 77: no default — the LO picks, or a linked loan prefills it
    county: '',
    program: 'Conventional',
    down_payment_pct: 20,
    loan_term_years: DEFAULT_TERM,
    prepaid_interest_days: 15,
    escrow_tax_months: 3,
    escrow_hoi_months: 3,
    manual_overrides: {
      origination_pct: 1.0,
      processing_fee: 495,
      underwriting_fee: 795,
      interest_rate_pct: 6.5,
    },
    borrower_name: '',
    borrower_email: '',
    property_address: '',
    tax_unit_ids: null,   // ticket 131: chosen city/school/special ids (server adds county + countywide)
    homestead: false,
    tax_lookup_status: null,
    tax_special_answer: null, // yes | no | unsure: "is this in a MUD, PID, or other special district?"
  });
  const [txCounties, setTxCounties] = useState(TX_COUNTIES_FALLBACK);
  const [taxStale, setTaxStale] = useState(false);
  const [panelKey, setPanelKey] = useState(0);
  const [worksheet, setWorksheet] = useState(null);
  const [loading, setLoading] = useState(false);
  const [savedId, setSavedId] = useState(null);
  const [savedList, setSavedList] = useState([]);
  const [showAllSaved, setShowAllSaved] = useState(false);  // ticket 198: "Recent worksheets" shows 10 until expanded
  const [sendOpen, setSendOpen] = useState(false);
  const [dsOpen, setDsOpen] = useState(false);
  // Ticket 202: the DocuSign button shows only when the owner turned the feature on and setup is done.
  const [docusignOn, setDocusignOn] = useState(false);
  // Ticket 216: and only to a seat with Edit deals; the server refuses the send without it (esign.send).
  const { user } = useAuth() || {};
  const canEsign = (user?.permissions || []).includes('loans.edit');
  const [licensedStates, setLicensedStates] = useState([]); // from /tenants/mine (ticket 77)
  const [dealId, setDealId] = useState(null);               // kept from an opened worksheet saved against a deal
  const [contact, setContact] = useState(null);           // ticket 155: linked contact {contact_id, display_name}
  const [contactForm, setContactForm] = useState(null);     // ticket 155: open "+ New contact" form: {initial, fill}
  const [savePrompt, setSavePrompt] = useState(null);       // ticket 155: post-send banner: {worksheetId, email, name, match?}
  const [savedLink, setSavedLink] = useState(null);         // ticket 155: contact_id stored on the saved worksheet (savedId)
  const [sentLock, setSentLock] = useState(null);           // ticket 198: server said the opened worksheet was already sent; holds its message
  const [openedDoc, setOpenedDoc] = useState(null);         // ticket 198: the worksheet last opened, for its decision card
  const linkReq = useRef(0);                                // ticket 155: bumped on every link change; a late contact fetch is dropped
  const savedIdRef = useRef(null);
  savedIdRef.current = savedId;
  const [params] = useSearchParams();

  // Ticket 198: the down payment shown is what the loan amount leaves over, so a saved
  // worksheet's loan amount is never overwritten by the 20% default.
  const dpAmount = useMemo(
    () => Math.max(0, Math.round(Number(inputs.purchase_price) - Number(inputs.loan_amount))),
    [inputs.purchase_price, inputs.loan_amount],
  );
  // Editing the price or the percent re-derives the loan amount; editing the loan amount re-derives the percent.
  const setPrice = (v) => setInputs((s) => ({ ...s, purchase_price: v, loan_amount: Number(v) - dpOf(v, s.down_payment_pct) }));
  const setPct = (v) => setInputs((s) => ({ ...s, down_payment_pct: v, loan_amount: Number(s.purchase_price) - dpOf(s.purchase_price, v) }));
  const setLoan = (v) => setInputs((s) => ({ ...s, loan_amount: v, down_payment_pct: pctFor(s.purchase_price, v) }));

  useEffect(() => {
    api.get('/fee-worksheet/list').then(({ data }) => setSavedList(data.worksheets || [])).catch(() => {});
    getCounties().then((d) => { if (d?.counties?.length) setTxCounties(d.counties); }).catch(() => {});
    api.get('/v2/org/features/status').then(({ data }) => setDocusignOn(!!data?.docusign)).catch(() => {});
    api.get('/tenants/mine').then(({ data }) => setLicensedStates((data?.licensed_states || []).map((s) => String(s).toUpperCase()))).catch(() => {});
    // eslint-disable-next-line
  }, []);

  // Ticket 198: a decision notification links here with ?worksheet=<id>.
  const worksheetParam = params.get('worksheet');
  useEffect(() => {
    if (!worksheetParam) return;
    if (!WORKSHEET_ID_RE.test(worksheetParam)) { toast.error('Could not open that worksheet'); return; }
    api.get(`/fee-worksheet/${worksheetParam}`)
      .then(({ data }) => openSaved(data))
      .catch((e) => toast.error(errorMessage(e, 'Could not open that worksheet')));
    // eslint-disable-next-line
  }, [worksheetParam]);

  // No auto-compute on mount any more: the state must be picked first.
  const stateOptions = (licensedStates.length ? licensedStates : US_STATES.map(([k]) => k));
  const countyOptions = inputs.state === 'TX' ? txCounties : (COUNTIES_BY_STATE[inputs.state] || null);
  const countyNoun = COUNTY_NOUN[inputs.state] || 'County';
  const stateCurated = !inputs.state || CURATED_STATES.includes(inputs.state);
  const requireState = () => {
    if (!inputs.state) { toast.error('Pick a state first'); return false; }
    return true;
  };

  const setField = (k, v) => setInputs((s) => ({ ...s, [k]: v }));

  // Ticket 155: the worksheet keeps its own copy of name/email; picking a contact
  // copies them in and links it. Editing them afterwards neither changes the
  // contact nor unlinks (only the chip's × does).
  const setLinked = (v) => { linkReq.current += 1; setContact(v); };
  // Show a stored link by name. A deleted contact is dropped (Save would 404 on it);
  // any other failure keeps the link under a placeholder name.
  const showLinked = (id, onFound) => {
    const req = ++linkReq.current;
    setContact({ contact_id: id, display_name: 'Linked contact' });
    uc.get(id).then((c) => {
      if (linkReq.current !== req) return;
      setContact({ contact_id: id, display_name: c.display_name || 'Linked contact' });
      onFound?.(c);
    }).catch((e) => {
      if (linkReq.current !== req || e?.response?.status !== 404) return;
      setContact(null);
      toast.error('The linked contact was deleted, so it was unlinked');
    });
  };
  const linkContact = (c, fill = true) => {
    setLinked({ contact_id: c.contact_id, display_name: c.display_name });
    if (fill) setInputs((s) => ({ ...s, borrower_name: c.display_name || '', borrower_email: primaryEmail(c) }));
  };
  // Link a contact to an already-saved worksheet (post-send prompt); the fields stay as sent.
  const linkSaved = async (worksheetId, c) => {
    try {
      await api.patch(`/fee-worksheet/${worksheetId}`, { contact_id: c.contact_id });
      if (worksheetId === savedIdRef.current) {
        setLinked({ contact_id: c.contact_id, display_name: c.display_name });
        setSavedLink(c.contact_id);
      }
      toast.success(`Linked ${c.display_name}`);
      api.get('/fee-worksheet/list').then(({ data }) => setSavedList(data.worksheets || [])).catch(() => {});
    } catch (e) {
      toast.error(errorMessage(e, 'Could not link the contact'));
    }
  };
  // Keyed on the link stored on the sent worksheet, not the form's unsaved chip.
  const afterSend = async (email, name) => {
    const worksheetId = savedIdRef.current;
    if (savedLink || !email) return;
    let match = null;
    try {
      const r = await uc.search(email, 8);
      const want = email.trim().toLowerCase();
      match = (r?.contacts || []).find((c) => (c.emails || []).some((e) => String(e.value || '').trim().toLowerCase() === want)) || null;
    } catch (_) { /* no lookup, offer to save instead */ }
    if (savedIdRef.current !== worksheetId) return; // another worksheet was opened meanwhile
    setSavePrompt({ worksheetId, email, name, match });
  };

  // "Open" on a saved worksheet loads it into the form above the fold; scroll
  // up and say so, otherwise it looks like nothing happened (ticket 77).
  const openSaved = (w) => {
    setInputs((s) => ({
      ...s, ...w.inputs,
      state: (w.inputs?.state || w.worksheet?.state || 'TX').toUpperCase(),
      // The tax rate belongs to the worksheet being opened, never to the form it replaces.
      manual_overrides: { ...withoutTaxRate(s.manual_overrides), ...(w.manual_overrides || {}) },
      tax_special_answer: w.inputs?.tax_special_answer ?? null,
      tax_unit_ids: w.inputs?.tax_unit_ids ?? null,
      homestead: Boolean(w.inputs?.homestead),
      tax_lookup_status: w.inputs?.tax_lookup_status ?? null,
      loan_term_years: w.inputs?.loan_term_years ?? DEFAULT_TERM,
      borrower_name: w.borrower_name || '', borrower_email: w.borrower_email || '', property_address: w.property_address || '',
    }));
    setPanelKey((k) => k + 1);
    setTaxStale(false);
    setSavedId(w.worksheet_id);
    setOpenedDoc(w);
    setSentLock(null);
    setDealId(w.deal_id || null);
    setWorksheet(w.worksheet);
    setSavePrompt(null);
    setContactForm(null);
    setSavedLink(w.contact_id || null);
    if (w.contact_id) showLinked(w.contact_id);
    else setLinked(null);
    try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch (_) { /* jsdom */ }
    toast.success(`Loaded ${w.property_address || w.borrower_name || 'worksheet'}`);
  };
  const setOverride = (k, v) => setInputs((s) => ({
    ...s, manual_overrides: { ...s.manual_overrides, [k]: v },
  }));

  // Ticket 131 tax panel plumbing.
  const CLEAR_TAX = { tax_unit_ids: null, homestead: false, tax_lookup_status: null, tax_special_answer: null };
  const changeTax = (patch) => { setInputs((s) => ({ ...s, ...patch })); setTaxStale(true); };
  const changeCounty = (v) => {
    setInputs((s) => (v === s.county ? s : { ...s, county: v, ...CLEAR_TAX }));
    setTaxStale(true);
  };
  const applyLookup = (patch, county) => {
    setInputs((s) => ({ ...s, ...patch, ...(county ? { county } : {}) }));
    setTaxStale(true);
  };
  const setManualTaxRate = (n) => {
    setInputs((s) => {
      const mo = { ...s.manual_overrides };
      if (n == null) delete mo.property_tax_rate_pct; else mo.property_tax_rate_pct = n;
      return { ...s, manual_overrides: mo };
    });
    setTaxStale(true);
  };
  const taxPayload = () => (inputs.state === 'TX' ? {
    tax_unit_ids: inputs.tax_unit_ids ?? null,
    homestead: Boolean(inputs.homestead),
    tax_lookup_status: inputs.tax_lookup_status ?? null,
    tax_special_answer: inputs.tax_special_answer ?? null,
  } : {});

  const computeEstimate = async () => {
    if (!requireState()) return;
    setLoading(true);
    try {
      const { data } = await api.post('/fee-worksheet/estimate', {
        purchase_price: Number(inputs.purchase_price),
        loan_amount: Number(inputs.loan_amount),
        state: inputs.state,
        county: inputs.county,
        program: inputs.program,
        down_payment_pct: Number(inputs.down_payment_pct),
        loan_term_years: Number(inputs.loan_term_years),
        prepaid_interest_days: Number(inputs.prepaid_interest_days),
        escrow_tax_months: Number(inputs.escrow_tax_months),
        escrow_hoi_months: Number(inputs.escrow_hoi_months),
        manual_overrides: Object.fromEntries(
          Object.entries(inputs.manual_overrides).map(([k, v]) => [k, Number(v)])
        ),
        ...taxPayload(),
      });
      setWorksheet(data);
      setTaxStale(false);
    } catch (e) {
      toast.error(errorMessage(e, 'Estimate failed'));
    } finally {
      setLoading(false);
    }
  };

  const refreshList = () => api.get('/fee-worksheet/list')
    .then(({ data }) => setSavedList(data.worksheets || []))
    .catch(() => {});

  // Ticket 198: Save writes over the opened worksheet; "Save as new copy" always makes another.
  const saveWorksheet = async (asCopy = false) => {
    if (!requireState()) return;
    setLoading(true);
    try {
      const payload = {
        purchase_price: Number(inputs.purchase_price),
        loan_amount: Number(inputs.loan_amount),
        state: inputs.state,
        county: inputs.county,
        deal_id: dealId || undefined,
        contact_id: contact?.contact_id || undefined,
        program: inputs.program,
        down_payment_pct: Number(inputs.down_payment_pct),
        loan_term_years: Number(inputs.loan_term_years),
        prepaid_interest_days: Number(inputs.prepaid_interest_days),
        escrow_tax_months: Number(inputs.escrow_tax_months),
        escrow_hoi_months: Number(inputs.escrow_hoi_months),
        manual_overrides: Object.fromEntries(
          Object.entries(inputs.manual_overrides).map(([k, v]) => [k, Number(v)])
        ),
        borrower_name: inputs.borrower_name || undefined,
        borrower_email: inputs.borrower_email || undefined,
        property_address: inputs.property_address || undefined,
        ...taxPayload(),
      };
      const inPlace = Boolean(savedId) && asCopy !== true;
      const { data } = inPlace
        ? await api.put(`/fee-worksheet/${savedId}`, payload)
        : await api.post('/fee-worksheet/save', payload);
      setWorksheet(data.worksheet);
      setTaxStale(false);
      setSavedId(data.worksheet_id);
      setSentLock(null);
      setSavedLink(contact?.contact_id || null);
      toast.success(asCopy === true ? 'Saved as a new copy' : 'Worksheet saved');
      await refreshList();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 409 && detail?.code === 'worksheet_sent') setSentLock(detail.message);
      else toast.error(errorMessage(e, 'Save failed'));
    } finally {
      setLoading(false);
    }
  };

  const turnOffLink = async (w) => {
    const ok = await confirmDialog({
      title: 'Turn off the borrower link?',
      description: `${w.borrower_name || 'The borrower'} will no longer be able to open or answer this estimate. Send to Borrower issues a new link.`,
      confirmText: 'Turn off link',
      destructive: true,
    });
    if (!ok) return;
    try {
      const { data } = await api.post(`/fee-worksheet/${w.worksheet_id}/revoke-link`);
      setSavedList((l) => l.map((x) => (x.worksheet_id === w.worksheet_id ? { ...x, link_revoked_at: data.link_revoked_at } : x)));
      toast.success('Borrower link turned off');
    } catch (e) {
      toast.error(errorMessage(e, 'Could not turn off the link'));
    }
  };

  const deleteSaved = async (w) => {
    const label = w.borrower_name || w.property_address || 'This worksheet';
    const ok = await confirmDialog({
      title: 'Delete this worksheet?',
      description: w.borrower_status && w.borrower_status !== 'draft'
        ? `${label} was sent to the borrower. Deleting it also turns off their link.`
        : `${label} will be removed from your list.`,
      confirmText: 'Delete',
      destructive: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/fee-worksheet/${w.worksheet_id}`);
      setSavedList((l) => l.filter((x) => x.worksheet_id !== w.worksheet_id));
      if (savedIdRef.current === w.worksheet_id) {
        // The form keeps its numbers; the next Save makes a new worksheet.
        setSavedId(null); setOpenedDoc(null); setSentLock(null); setSavePrompt(null); setSavedLink(null);
      }
      toast.success('Worksheet deleted');
    } catch (e) {
      toast.error(errorMessage(e, 'Delete failed'));
    }
  };

  const opened = savedList.find((w) => w.worksheet_id === savedId)
    || (openedDoc && openedDoc.worksheet_id === savedId ? openedDoc : null);

  return (
    <div className="p-4 md:p-8 max-w-7xl mx-auto" data-testid="fee-worksheet-page">
      {/* Header */}
      <div className="mb-6 flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-serif text-3xl font-bold flex items-center gap-2" style={{ color: NAVY }}>
            <Calculator /> Fee Worksheet
          </h1>
          <p className="text-slate-500 mt-1 text-sm">
            Closing cost estimate (curated for TX; other states use placeholder rates) → editable line items → Loan Estimate → borrower approval.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={computeEstimate}
            disabled={loading}
            data-testid="btn-recalc"
            className="inline-flex items-center gap-2 px-4 h-10 whitespace-nowrap rounded-md text-sm font-medium border border-slate-200 hover:bg-slate-50"
          >
            <RefreshCcw size={13} /> Recalculate
          </button>
          <button
            onClick={() => saveWorksheet()}
            disabled={loading}
            data-testid="btn-save"
            className="inline-flex items-center gap-2 px-4 h-10 whitespace-nowrap rounded-md text-white text-sm font-medium disabled:opacity-60"
            style={{ background: NAVY }}
          >
            {loading ? <Loader2 className="animate-spin" size={13} /> : <Save size={13} />} Save
          </button>
          {savedId && (
            <button
              onClick={() => saveWorksheet(true)}
              disabled={loading}
              data-testid="btn-save-copy"
              className="inline-flex items-center gap-2 px-4 h-10 whitespace-nowrap rounded-md text-sm font-medium border border-slate-200 hover:bg-slate-50 disabled:opacity-60"
            >
              <Copy size={13} /> Save as new copy
            </button>
          )}
          <button
            onClick={async () => {
              if (!savedId) { toast.error('Save the worksheet first'); return; }
              try {
                const resp = await api.get(`/fee-worksheet/${savedId}/pdf`, { responseType: 'blob' });
                const url = URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
                const a = document.createElement('a');
                a.href = url;
                a.download = `loan-estimate-${savedId}.pdf`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                URL.revokeObjectURL(url);
              } catch (e) {
                toast.error('Failed to download PDF');
              }
            }}
            aria-disabled={!savedId}
            title={savedId ? undefined : 'Save the worksheet first'}
            data-testid="btn-download-pdf"
            className={`inline-flex items-center gap-2 px-4 h-10 whitespace-nowrap rounded-md text-sm font-medium border border-slate-200 hover:bg-slate-50 ${savedId ? '' : 'opacity-40'}`}
          >
            <Download size={13} /> LE PDF
          </button>
          {docusignOn && canEsign && (
          <button
            onClick={() => savedId ? setDsOpen(true) : toast.error('Save the worksheet first')}
            disabled={loading || !savedId}
            data-testid="btn-docusign"
            className="inline-flex items-center gap-2 px-4 h-10 whitespace-nowrap rounded-md text-sm font-medium border transition disabled:opacity-40"
            style={{ borderColor: NAVY, color: NAVY }}
          >
            <FileSignature size={13} /> DocuSign
          </button>
          )}
          <button
            onClick={() => savedId ? setSendOpen(true) : toast.error('Save the worksheet first')}
            disabled={loading || !savedId}
            data-testid="btn-send-borrower"
            className="inline-flex items-center gap-2 px-4 h-10 whitespace-nowrap rounded-md text-white text-sm font-medium disabled:opacity-60"
            style={{ background: GREEN }}
          >
            <Send size={13} /> Send to Borrower
          </button>
        </div>
      </div>

      {sentLock && (
        <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 text-amber-800 text-sm px-3 py-2 flex items-center justify-between gap-3 flex-wrap" data-testid="ws-sent-banner">
          <span>{sentLock}</span>
          <button type="button" onClick={() => saveWorksheet(true)} disabled={loading} data-testid="btn-save-copy-banner"
            className="h-8 px-3 rounded-md text-white text-xs font-semibold disabled:opacity-60" style={{ background: NAVY }}>
            Save as new copy
          </button>
        </div>
      )}
      {opened && DECIDED.includes(opened.borrower_status) && (
        <div className="mb-4 rounded-md border px-3 py-2 text-sm"
          style={opened.borrower_status === 'approved'
            ? { background: '#dcfce7', borderColor: '#86efac', color: '#166534' }
            : { background: '#fee2e2', borderColor: '#fca5a5', color: '#991b1b' }}
          data-testid="opened-decision">
          <div className="font-semibold">
            {opened.borrower_status === 'approved' ? 'The borrower approved this estimate' : 'The borrower sent this estimate back'}
            {decidedAt(opened.borrower_decided_at) ? ` on ${decidedAt(opened.borrower_decided_at)}` : ''}
          </div>
          {opened.borrower_comment && <p className="mt-1 whitespace-pre-wrap break-words" data-testid="opened-decision-comment">{opened.borrower_comment}</p>}
        </div>
      )}

      <div className="grid lg:grid-cols-[380px_1fr] gap-5">
        {/* Inputs pane */}
        <div className="space-y-4">
          <Card title="Loan basics" testid="card-basics">
            <MoneyField label="Purchase price" value={inputs.purchase_price} onChange={setPrice} testid="fld-price" />
            <MoneyField label="Loan amount" value={inputs.loan_amount} onChange={setLoan} testid="fld-loan" />
            <PctField label={`Down payment (${fmt$(dpAmount)})`} value={inputs.down_payment_pct} onChange={setPct} testid="fld-dp" />
            <PctField label="Note rate" value={inputs.manual_overrides.interest_rate_pct} onChange={(v) => setOverride('interest_rate_pct', v)} testid="fld-rate" />
            <PickSelect label="Loan term" value={String(inputs.loan_term_years ?? DEFAULT_TERM)} options={TERM_OPTIONS}
              onChange={(v) => setField('loan_term_years', Number(v))} testId="fld-term" />
            <SelectField label="State" value={inputs.state} options={stateOptions} placeholder="Select state…"
              onChange={(v) => setInputs((s) => ({ ...s, state: v, county: '', ...CLEAR_TAX, ...(v === 'TX' ? {} : { manual_overrides: withoutTaxRate(s.manual_overrides) }) }))} testid="fld-state" />
            {licensedStates.length === 0 && (
              <p className="text-[11px] text-slate-400 -mt-1" data-testid="fld-state-hint">Set your licensed states under Settings → Branding to shorten this list.</p>
            )}
            {inputs.state && !stateCurated && (
              <div className="rounded-md border border-amber-200 bg-amber-50 text-amber-800 text-[12px] px-3 py-2" data-testid="fee-state-placeholder-banner">
                Fee tables aren't curated for {inputs.state} yet — Texas rates are shown as a placeholder. Verify every line before sending.
              </div>
            )}
            {inputs.state && (countyOptions ? (
              <SelectField label={countyNoun} value={inputs.county} options={countyOptions} placeholder={`Select ${countyNoun.toLowerCase()}…`}
                onChange={inputs.state === 'TX' ? changeCounty : (v) => setField('county', v)} testid="fld-county" />
            ) : (
              <TextField label="County" value={inputs.county} onChange={(v) => setField('county', v)} testid="fld-county" />
            ))}
            <SelectField label="Program" value={inputs.program} options={PROGRAMS} onChange={(v) => setField('program', v)} testid="fld-program" />
          </Card>

          <Card title="LO overrides" testid="card-overrides">
            <PctField label="Origination points" value={inputs.manual_overrides.origination_pct} onChange={(v) => setOverride('origination_pct', v)} testid="fld-orig" />
            <MoneyField label="Processing fee" value={inputs.manual_overrides.processing_fee} onChange={(v) => setOverride('processing_fee', v)} testid="fld-processing" />
            <MoneyField label="Underwriting fee" value={inputs.manual_overrides.underwriting_fee} onChange={(v) => setOverride('underwriting_fee', v)} testid="fld-underwriting" />
            <div className="grid grid-cols-3 gap-2">
              <NumField label="Interest days" value={inputs.prepaid_interest_days} onChange={(v) => setField('prepaid_interest_days', v)} testid="fld-idays" />
              <NumField label="Tax mo. reserve" value={inputs.escrow_tax_months} onChange={(v) => setField('escrow_tax_months', v)} testid="fld-taxmo" />
              <NumField label="HOI mo. reserve" value={inputs.escrow_hoi_months} onChange={(v) => setField('escrow_hoi_months', v)} testid="fld-hoimo" />
            </div>
          </Card>

          <Card title="Borrower + property" testid="card-borrower">
            <label className="block">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Borrower name</span>
              <ContactSearchInput
                value={inputs.borrower_name} onChange={(v) => setField('borrower_name', v)} testid="fld-bname"
                onPick={(c) => linkContact(c)}
                onCreateNew={(text) => setContactForm({ initial: contactSeed(text), fill: true })}
                className="mt-1 w-full h-9 rounded-md border border-slate-200 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#6BA82E]/40"
              />
            </label>
            <TextField label="Borrower email" value={inputs.borrower_email} onChange={(v) => setField('borrower_email', v)} testid="fld-bemail" type="email" />
            {contact && (
              <div className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-[12px] text-slate-700" data-testid="borrower-linked-chip">
                Linked: {contact.display_name}
                <button type="button" onClick={() => setLinked(null)} aria-label="Unlink contact"
                  className="ml-1 text-slate-500 hover:text-slate-900" data-testid="borrower-unlink">×</button>
              </div>
            )}
            {savePrompt && !contactForm && (
              <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-[12px] text-slate-700 flex items-center justify-between gap-2" data-testid="ws-save-contact-prompt">
                <span>
                  {savePrompt.match
                    ? `${savePrompt.email} is ${savePrompt.match.display_name} in your contacts — link this worksheet?`
                    : `Save ${savePrompt.name || savePrompt.email} as a contact?`}
                </span>
                <span className="flex gap-1 shrink-0">
                  {savePrompt.match ? (
                    <button type="button" data-testid="ws-save-contact-link"
                      onClick={() => { linkSaved(savePrompt.worksheetId, savePrompt.match); setSavePrompt(null); }}
                      className="h-7 px-2 font-semibold rounded-md text-white" style={{ background: NAVY }}>Link</button>
                  ) : (
                    <button type="button" data-testid="ws-save-contact-save"
                      onClick={() => setContactForm({ initial: { ...contactSeed(savePrompt.name), email: savePrompt.email }, fill: false, worksheetId: savePrompt.worksheetId })}
                      className="h-7 px-2 font-semibold rounded-md text-white" style={{ background: NAVY }}>Save as contact</button>
                  )}
                  <button type="button" data-testid="ws-save-contact-dismiss" onClick={() => setSavePrompt(null)}
                    className="h-7 px-2 font-semibold rounded-md text-slate-600 hover:bg-slate-100">Not now</button>
                </span>
              </div>
            )}
            {contactForm && (
              <QuickContactForm
                defaultType="borrower" testIdPrefix="ws-new-contact" initial={contactForm.initial}
                onCancel={() => setContactForm(null)}
                onCreated={(c) => {
                  if (contactForm.fill) linkContact(c); else linkSaved(contactForm.worksheetId, c);
                  setContactForm(null); setSavePrompt(null);
                }}
                onExisting={(c) => {
                  if (contactForm.fill) linkContact(c); else linkSaved(contactForm.worksheetId, c);
                  setContactForm(null); setSavePrompt(null);
                }}
              />
            )}
            <label className="block">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Property address</span>
              <AddressAutocomplete
                value={inputs.property_address} onChange={(v) => setField('property_address', v)} state={inputs.state} testid="fld-addr"
                className="mt-1"
              />
            </label>
            <TaxLookupPanel
              key={panelKey}
              state={inputs.state}
              address={inputs.property_address || ''}
              county={inputs.county}
              counties={txCounties}
              value={inputs}
              onChange={changeTax}
              onLookupApplied={applyLookup}
              onCountyChange={changeCounty}
              manualRate={inputs.manual_overrides.property_tax_rate_pct ?? null}
              onManualRateChange={setManualTaxRate}
              taxBasis={worksheet?.tax_basis || null}
              stale={taxStale}
            />
          </Card>
        </div>

        {/* Worksheet pane */}
        <div>
          {!worksheet ? (
            <div className="p-10 text-center text-slate-500 bg-white rounded-2xl border border-slate-100">
              <Sparkles size={20} className="mx-auto text-slate-300" />
              <p className="mt-2 text-sm">{inputs.state ? 'Configure loan basics then click Recalculate.' : 'Pick a state, then click Recalculate.'}</p>
            </div>
          ) : (
            <>
              <TotalsCard w={worksheet} dp={dpAmount} testid="totals" />
              <div className="mt-4 space-y-3">
                {worksheet.sections.map((s) => <SectionCard key={s.name} section={s} />)}
              </div>
              <p className="text-[11px] text-slate-400 mt-3 italic">{worksheet.source_note}</p>
            </>
          )}
        </div>
      </div>

      {savedList.length > 0 && (
        <section className="mt-10">
          <h2 className="font-serif text-lg font-semibold mb-3" style={{ color: NAVY }}>Recent worksheets</h2>
          <div className="space-y-2">
            {(showAllSaved ? savedList : savedList.slice(0, SAVED_PREVIEW)).map((w) => <SavedRow key={w.worksheet_id} ws={w} onSelect={() => openSaved(w)} onDelete={() => deleteSaved(w)} onTurnOff={() => turnOffLink(w)} />)}
          </div>
          {savedList.length > SAVED_PREVIEW && (
            <button
              type="button"
              onClick={() => setShowAllSaved((v) => !v)}
              className="mt-3 text-xs px-3 h-8 rounded-md border border-slate-200 hover:bg-slate-50"
              data-testid="saved-toggle"
            >
              {showAllSaved ? 'Show fewer' : `Show all ${savedList.length}`}
            </button>
          )}
        </section>
      )}

      {sendOpen && savedId && (
        <SendBorrowerModal
          worksheetId={savedId}
          defaults={{ borrower_email: inputs.borrower_email, borrower_name: inputs.borrower_name }}
          linked={Boolean(opened?.deal_id)}
          onClose={() => setSendOpen(false)}
          onSent={async (res, sent) => {
            setSendOpen(false);
            refreshList(); // the row now reads "Sent to borrower"
            afterSend(sent?.email, sent?.name); // ticket 155: offer to link / save the recipient (never auto-creates)
            if (res.delivered_via_email) {
              toast.success('Emailed borrower');
            } else if (await copyText(res.review_url)) {
              toast.success('Link copied to clipboard');
            } else {
              toast.error('Copy failed — select the text and copy it manually');
            }
          }}
        />
      )}

      {docusignOn && canEsign && dsOpen && savedId && (
        <DocuSignEnvelopeModal
          worksheetId={savedId}
          dealId={null}
          defaultSubject={`Loan Estimate — ${inputs.borrower_name || 'borrower'} sign & return`}
          onClose={() => setDsOpen(false)}
          onSent={() => setDsOpen(false)}
        />
      )}
    </div>
  );
}

// ==================================================================
// Cards + inputs
// ==================================================================
function Card({ title, children, testid }) {
  return (
    <div className="bg-white rounded-xl border border-slate-100 p-4" data-testid={testid}>
      <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-3">{title}</div>
      <div className="space-y-3">{children}</div>
    </div>
  );
}
function TextField({ label, value, onChange, testid, type }) {
  return (
    <label className="block">
      <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</span>
      <Input type={type || 'text'} value={value ?? ''} onChange={(e) => onChange(e.target.value)}
             data-testid={testid}
             className="mt-1" />
    </label>
  );
}
function MoneyField({ label, value, onChange, testid }) {
  return (
    <label className="block">
      <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</span>
      <div className="mt-1 flex items-center rounded-md border border-slate-200 focus-within:ring-2 focus-within:ring-[#6BA82E]/40">
        <MoneyInput cents value={value} onChange={(n) => onChange(n == null ? '' : String(n))}
               data-testid={testid}
               className="h-9 text-sm border-0 shadow-none focus-visible:ring-0" />
      </div>
    </label>
  );
}
function PctField({ label, value, onChange, testid }) {
  return (
    <label className="block">
      <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</span>
      <div className="mt-1 flex items-center rounded-md border border-slate-200 focus-within:ring-2 focus-within:ring-[#6BA82E]/40">
        <NumberInput value={value} onChange={(n) => onChange(n == null ? '' : String(n))}
               data-testid={testid}
               className="w-full h-9 px-3 text-sm border-0 shadow-none focus-visible:ring-0" />
        <span className="pr-3 text-slate-400 text-xs">%</span>
      </div>
    </label>
  );
}
function NumField({ label, value, onChange, testid }) {
  return (
    <label className="block">
      <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</span>
      <NumberInput integer value={value} onChange={(n) => onChange(n == null ? '' : String(n))}
             data-testid={testid}
             className="mt-1 w-full h-9 rounded-md border border-slate-200 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#6BA82E]/40" />
    </label>
  );
}
function SelectField({ label, value, options, onChange, testid, placeholder }) {
  return (
    <PickSelect
      label={label} value={value || ''} onChange={onChange} testId={testid} placeholder={placeholder}
      options={options.map((o) => ({ value: o, label: o }))}
    />
  );
}

function TotalsCard({ w, dp, testid }) {
  return (
    <div className="rounded-2xl p-5 text-white grid grid-cols-2 md:grid-cols-4 gap-3" style={{ background: NAVY }} data-testid={testid}>
      <Metric label="Down payment" value={fmt$(dp)} />
      <Metric label="Closing costs (J)" value={fmt$(w.totals.section_j_total_closing_costs)} testid="metric-closing" />
      <Metric label="Cash to close" value={fmt$(w.totals.estimated_cash_to_close)} accent testid="metric-ctc" />
      <Metric label="Est. monthly TI" value={fmt$(w.totals.estimated_monthly_taxes + w.totals.estimated_monthly_hoi)} />
    </div>
  );
}
function Metric({ label, value, accent, testid }) {
  return (
    <div data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider" style={{ color: accent ? LIME : 'rgba(255,255,255,0.7)' }}>{label}</div>
      <div className="font-serif text-2xl font-bold mt-0.5" style={{ color: accent ? LIME : 'white' }}>{value}</div>
    </div>
  );
}

function SectionCard({ section }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="bg-white rounded-xl border border-slate-100 overflow-hidden" data-testid={`section-${section.name.split('.')[0]}`}>
      <button onClick={() => setOpen(!open)} className="w-full flex items-center gap-2 px-4 py-3 hover:bg-slate-50">
        {open ? <ChevronDown size={14} className="text-slate-400" /> : <ChevronRight size={14} className="text-slate-400" />}
        <span className="text-sm font-semibold flex-1 text-left" style={{ color: NAVY }}>{section.name}</span>
        <span className="text-sm font-mono font-semibold" style={{ color: NAVY }}>{fmt$2(section.subtotal)}</span>
      </button>
      {open && (
        <div className="border-t border-slate-100 divide-y divide-slate-50">
          {section.items.map((it, i) => (
            <div key={i} className="px-4 py-2 flex items-baseline gap-2 hover:bg-slate-50/50">
              <span className="text-sm text-slate-700 flex-1">{it.name}</span>
              <span className="text-[10px] uppercase tracking-wider text-slate-400 shrink-0">{it.source}</span>
              <span className="text-sm font-mono w-24 text-right shrink-0" style={{ color: it.amount === 0 ? '#94a3b8' : NAVY }}>{fmt$2(it.amount)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SavedRow({ ws, onSelect, onDelete, onTurnOff }) {
  const linkOpen = ['sent', 'viewed'].includes(ws.borrower_status);
  const linkOff = linkOpen && Boolean(ws.link_revoked_at);
  const STATUS = {
    draft:    { bg: '#f1f5f9', fg: '#334155', label: 'Draft' },
    sent:     { bg: '#eff6ff', fg: '#1e40af', label: 'Sent to borrower' },
    viewed:   { bg: '#fef3c7', fg: '#92400e', label: 'Viewed' },
    approved: { bg: '#dcfce7', fg: '#166534', label: 'Approved' },
    rejected: { bg: '#fee2e2', fg: '#991b1b', label: 'Rejected' },
  }[ws.borrower_status] || { bg: '#f1f5f9', fg: '#334155', label: ws.borrower_status };
  if (linkOff) Object.assign(STATUS, { bg: '#f1f5f9', fg: '#334155', label: 'Link turned off' });
  return (
    <div className="bg-white rounded-xl border border-slate-100 p-3 flex items-center gap-3" data-testid={`saved-row-${ws.worksheet_id}`}>
      <div className="w-9 h-9 rounded-full flex items-center justify-center shrink-0" style={{ background: '#eff6ff' }}>
        <Home size={16} style={{ color: NAVY }} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold" style={{ color: NAVY }}>
          {ws.borrower_name || 'Unnamed borrower'} · {fmt$(ws.inputs?.purchase_price || 0)}
        </div>
        <div className="text-xs text-slate-500 truncate">
          {ws.property_address || '—'} · {[ws.inputs?.state || ws.worksheet?.state, ws.inputs?.county].filter(Boolean).join(' / ') || '—'} · {ws.inputs?.program}
        </div>
        {DECIDED.includes(ws.borrower_status) && (
          <div className="text-xs text-slate-600 mt-0.5 break-words" data-testid={`saved-decision-${ws.worksheet_id}`}>
            {decidedAt(ws.borrower_decided_at) || ''}
            {ws.borrower_comment ? <span className="whitespace-pre-wrap">{decidedAt(ws.borrower_decided_at) ? ' · ' : ''}{ws.borrower_comment}</span> : null}
          </div>
        )}
      </div>
      <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded" style={{ background: STATUS.bg, color: STATUS.fg }}>
        {STATUS.label}
      </span>
      <button onClick={onSelect} className="text-xs px-3 h-8 rounded-md border border-slate-200 hover:bg-slate-50" data-testid={`open-${ws.worksheet_id}`}>Open</button>
      {linkOpen && !linkOff && (
        <button onClick={onTurnOff} className="text-xs px-3 h-8 rounded-md border border-slate-200 hover:bg-slate-50" data-testid={`turn-off-${ws.worksheet_id}`}>Turn off link</button>
      )}
      <button onClick={onDelete} aria-label="Delete worksheet" className="text-xs px-2 h-8 rounded-md border border-slate-200 text-red-700 hover:bg-red-50 inline-flex items-center gap-1" data-testid={`delete-${ws.worksheet_id}`}>
        <Trash2 size={12} /> Delete
      </button>
    </div>
  );
}

function SendBorrowerModal({ worksheetId, defaults, linked = false, onClose, onSent }) {
  const [email, setEmail] = useState(defaults.borrower_email || '');
  const [name, setName] = useState(defaults.borrower_name || '');
  const [msg, setMsg] = useState('');
  const [sending, setSending] = useState(false);
  const submit = async () => {
    if (!email) return toast.error('Borrower email required');
    setSending(true);
    try {
      const { data } = await api.post(`/fee-worksheet/${worksheetId}/send-borrower`, {
        borrower_email: email, borrower_name: name, message: msg,
      });
      onSent(data, { email, name });
    } catch (e) {
      // Ticket 167: the interceptor already toasted the approval message.
      if (!isSpendApprovalError(e)) toast.error(errorMessage(e, 'Send failed'));
    } finally {
      setSending(false);
    }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="send-borrower-modal">
        <h3 className="font-serif text-lg font-semibold flex items-center gap-2" style={{ color: NAVY }}><Send size={16} /> Send worksheet to borrower</h3>
        <p className="text-xs text-slate-500 mt-1" data-testid="send-validity">
          Borrower gets a secure link to review + approve ({linked ? 'valid while the loan is open' : '14-day expiry'}).
        </p>
        <div className="mt-4 space-y-3">
          <TextField label="Borrower email *" value={email} onChange={setEmail} testid="send-email" type="email" />
          <TextField label="Borrower name" value={name} onChange={setName} testid="send-name" />
          <label className="block">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Note (optional)</span>
            <textarea value={msg} onChange={(e) => setMsg(e.target.value)} rows={2}
                      data-testid="send-message"
                      className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#6BA82E]/40" />
          </label>
        </div>
        <div className="mt-5 flex justify-between">
          <button onClick={onClose} className="text-sm px-4 h-9 rounded-md border border-slate-200 hover:bg-slate-50">Cancel</button>
          <button onClick={submit} disabled={sending} data-testid="send-submit"
                  className="text-sm px-4 h-9 rounded-md text-white font-medium inline-flex items-center gap-2 disabled:opacity-60"
                  style={{ background: GREEN }}>
            {sending ? <Loader2 className="animate-spin" size={13} /> : <Send size={13} />} Send
          </button>
        </div>
      </div>
    </div>
  );
}
